/**
 * "Suggest changes" mode for the flow designer.
 *
 * While active, the user edits the flow freely but nothing touches the live
 * shared draft — the designer's edit mutations are shimmed to resolve locally
 * (see `stagedMutations.ts`). On Submit the staged canvas state is serialized
 * into a snapshot and posted as a `user`-sourced proposal, which then appears in
 * the proposals pill/tray for normal review/accept. On Discard (or after a
 * successful submit) the canvas is restored to the live draft.
 *
 * @module FlowDesigner/proposals/useSuggestMode
 */

import React from 'react';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import type { UndoHistoryHandle } from '../hooks/useUndoHistory';
import { fromClientState } from './diffSnapshot';
import { useCreateProposal } from './useWorkflowProposals';
import type { DiffSnapshot } from '@chatsdet/workflow-diff';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Replace any non-UUID node/edge id with a fresh UUID, rewriting edge endpoints
 * so the graph stays consistent. The staging shim already mints UUIDs for most
 * edits; this is the safety net guaranteeing the snapshot passes the server's
 * UUID validation regardless of any leftover optimistic temp ids.
 */
export function sanitizeSnapshotIds(snapshot: DiffSnapshot): DiffSnapshot {
  const idMap = new Map<string, string>();
  const remap = (id: string): string => {
    if (UUID_RE.test(id)) return id;
    let next = idMap.get(id);
    if (!next) {
      next = crypto.randomUUID();
      idMap.set(id, next);
    }
    return next;
  };

  const nodes = snapshot.nodes.map((n) => ({ ...n, id: remap(n.id) }));
  const edges = snapshot.edges.map((e) => ({
    ...e,
    id: UUID_RE.test(e.id) ? e.id : crypto.randomUUID(),
    sourceNodeId: remap(e.sourceNodeId),
    targetNodeId: remap(e.targetNodeId),
  }));
  return { nodes, edges };
}

export interface SuggestModeHandle {
  isSuggesting: boolean;
  /** Enter suggest mode, snapshotting the current live canvas as the baseline. */
  enter: () => void;
  /** Leave without submitting, restoring the baseline. */
  discard: () => void;
  /** Serialize staged edits → proposal, then restore the baseline. */
  submit: (params: { title: string; description?: string }) => Promise<void>;
  isSubmitting: boolean;
}

export function useSuggestMode(params: {
  workflowId: string | null;
  /**
   * Shared mode flag, owned by the parent so the mutation shim (created before
   * this hook in render order) and this hook read/write the same handle.
   */
  suggestingRef: React.MutableRefObject<boolean>;
  getNodes: () => WorkflowNode[];
  getEdges: () => WorkflowEdge[];
  setNodes: (nodes: WorkflowNode[]) => void;
  setEdges: (edges: WorkflowEdge[]) => void;
  undoHistory: UndoHistoryHandle;
  toast: { success: (msg: string) => void; error: (msg: string) => void };
}): SuggestModeHandle {
  const { workflowId, suggestingRef, getNodes, getEdges, setNodes, setEdges, undoHistory, toast } =
    params;

  const [isSuggesting, setIsSuggesting] = React.useState(false);
  const baselineRef = React.useRef<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] } | null>(null);

  const createProposal = useCreateProposal(workflowId ?? null);

  const restoreBaseline = React.useCallback(() => {
    const baseline = baselineRef.current;
    if (baseline) {
      setNodes(baseline.nodes);
      setEdges(baseline.edges);
    }
    baselineRef.current = null;
    undoHistory.clearHistory();
  }, [setNodes, setEdges, undoHistory]);

  const enter = React.useCallback(() => {
    if (suggestingRef.current) return;
    baselineRef.current = { nodes: getNodes(), edges: getEdges() };
    // Flip the ref first so any edit fired in the same tick stages locally.
    suggestingRef.current = true;
    setIsSuggesting(true);
    undoHistory.clearHistory();
  }, [getNodes, getEdges, undoHistory]);

  const discard = React.useCallback(() => {
    if (!suggestingRef.current) return;
    suggestingRef.current = false;
    setIsSuggesting(false);
    restoreBaseline();
  }, [restoreBaseline]);

  const submit = React.useCallback(
    async ({ title, description }: { title: string; description?: string }) => {
      if (!workflowId || !suggestingRef.current) return;
      const snapshot = sanitizeSnapshotIds(fromClientState(getNodes(), getEdges()));
      try {
        await createProposal.mutateAsync({ title, description, snapshotJson: snapshot });
        suggestingRef.current = false;
        setIsSuggesting(false);
        restoreBaseline();
        toast.success('Changes submitted as a proposal for review.');
      } catch (err) {
        // Stay in suggest mode so the user can fix and retry or discard.
        toast.error(
          err instanceof Error ? err.message : 'Could not submit changes.'
        );
        throw err;
      }
    },
    [workflowId, getNodes, getEdges, createProposal, restoreBaseline, toast]
  );

  return {
    isSuggesting,
    enter,
    discard,
    submit,
    isSubmitting: createProposal.isPending,
  };
}
